// Layer 3 — the Diagnosis and Action stage bodies under the Advisory strip
// (polish.P28b, #715; comp `.impeccable/mocks/decision/advisory-stages.html`,
// family A with no new comp: Diagnosis = candidates 8 · investigation runs 4,
// Action = cases 8 · gate queue 4). Pure projections of `flowLandingRead()`:
// every list here is the list its strip cell counts, or a subset of it.

import type { Case, RecommendedAction } from '@/versions/v1/schema/case';
import type { Persona } from '@/shared/types';
import { ZERO_TOUCH } from './branch-labels';
import { projectionForPersona } from './DriverTree/Scene.transform';
import type { FlowCaseRow, FlowLandingRead } from './FlowLanding.transform';

/**
 * Comp adjudication 7, extended by #731 to the whole module: the floor seats —
 * the Operator and the Control room, the driver tree's same floor projection —
 * read no US$ anywhere on Advisory: the value band, the pipeline strip, the
 * stage bodies and the case page. One rule, one home; every surface asks this.
 */
export function advisoryShowsMoney(persona: Persona): boolean {
  return projectionForPersona(persona) !== 'operator';
}

/** The stage bodies' own name for the one module rule above. */
export function stageBodiesShowMoney(persona: Persona): boolean {
  return advisoryShowsMoney(persona);
}

// ---------------------------------------------------------------------------
// Cases (the open-case table beside the role screen)
// ---------------------------------------------------------------------------

const SEVERITY_RANK: Readonly<Record<Case['severity'], number>> = { high: 3, med: 2, low: 1, monitor: 0 };

/**
 * The order the open-case table draws for this seat. A seat that reads US$
 * keeps the flow read's order (priced cases by US$/day, then unpriced by
 * severity); a floor seat reads no US$, so its rows order by what it can act
 * on: most severe first, then oldest (fix-p28b-r1 item 7).
 */
export function casesTableRows(rows: readonly FlowCaseRow[], showMoney: boolean): readonly FlowCaseRow[] {
  if (showMoney) return rows;
  return [...rows].sort((a, b) =>
    SEVERITY_RANK[b.caseItem.severity] - SEVERITY_RANK[a.caseItem.severity] ||
    a.caseItem.raisedAt.localeCompare(b.caseItem.raisedAt) ||
    a.caseItem.title.localeCompare(b.caseItem.title));
}

/** The table's meta line names the order `casesTableRows` draws. */
export function casesTableMeta(atCasesCount: number, showMoney: boolean): string {
  return showMoney
    ? `${atCasesCount} at Cases · priced cases first, then unpriced by severity`
    : `${atCasesCount} at Cases · most severe first, then oldest`;
}

// ---------------------------------------------------------------------------
// Diagnosis
// ---------------------------------------------------------------------------

export type DiagnosisCandidateRow = {
  caseId: string;
  title: string;
  /** `ZN-RGH-PH · raised 1d 8h 26m ago`. */
  meta: string;
  circuit: string;
  /** The case's leading candidate cause, or the zero-touch wording. */
  candidate: string;
  zeroTouch: boolean;
};

/** The 8: each case the Diagnosis cell counts, with its leading candidate. */
export function diagnosisCandidateRows(read: Pick<FlowLandingRead, 'diagnosisRows' | 'openCaseRows'>): DiagnosisCandidateRow[] {
  return read.diagnosisRows.map(({ caseItem, causeLabel }) => {
    const flow = read.openCaseRows.find((row) => row.caseItem.id === caseItem.id);
    const zeroTouch = caseItem.branch === 'zero-touch';
    const code = caseItem.code ?? caseItem.id;
    return {
      caseId: caseItem.id,
      title: caseItem.title,
      meta: flow ? `${code} · raised ${flow.age} ago` : code,
      circuit: caseItem.circuit,
      candidate: zeroTouch ? ZERO_TOUCH.label : causeLabel,
      zeroTouch,
    };
  });
}

// ---------------------------------------------------------------------------
// Action
// ---------------------------------------------------------------------------

/** The Action statuses that wait on a decision at a gate (D19 / K13b). */
const GATE_STATUSES: ReadonlySet<FlowCaseRow['actionStatus']> = new Set([
  'awaiting approval',
  'FMEA proposal · Senior met',
]);

export type GateQueueRow = {
  caseId: string;
  title: string;
  code: string;
  status: FlowCaseRow['actionStatus'];
  /** The seat that decides: the case's acting human, or the Senior met seat for an FMEA proposal. */
  seat: string;
  /** The first proposed action the gate decides, when the case names one. */
  proposal?: string;
};

function proposalForCase(caseItem: Case, actions: readonly RecommendedAction[]): string | undefined {
  const proposed = actions
    .filter((action) => action.caseId === caseItem.id && action.status === 'proposed')
    .sort((a, b) => (a.sequence ?? Number.POSITIVE_INFINITY) - (b.sequence ?? Number.POSITIVE_INFINITY));
  return proposed[0]?.text;
}

/**
 * The 4: the Action cases waiting on a gate decision, in the Action body's own
 * order. Each row is one of the cell's `awaiting approval` or `FMEA proposal ·
 * Senior met` rows, so the queue is a subset of the 8 beside it.
 */
export function gateQueueRows(
  actionRows: readonly FlowCaseRow[],
  actions: readonly RecommendedAction[],
): GateQueueRow[] {
  return actionRows
    .filter((row) => GATE_STATUSES.has(row.actionStatus))
    .map((row) => ({
      caseId: row.caseItem.id,
      title: row.caseItem.title,
      code: row.caseItem.code ?? row.caseItem.circuit,
      status: row.actionStatus,
      seat: row.actionStatus === 'FMEA proposal · Senior met' ? 'Senior met' : row.actor,
      proposal: proposalForCase(row.caseItem, actions),
    }));
}
